import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const WarehouseContext = createContext();

export const useWarehouse = () => {
  const context = useContext(WarehouseContext);
  if (!context) {
    throw new Error('useWarehouse must be used within a WarehouseProvider');
  }
  return context;
};

export const WarehouseProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { clinic } = useAuth(); // Get clinic from AuthContext

  const fetchItems = async () => {
    if (!clinic) {
      if (import.meta.env.DEV) console.log('WarehouseContext: No clinic data, skipping fetch');
      return;
    }

    if (import.meta.env.DEV) console.log('WarehouseContext: Fetching items for clinic:', clinic.id);
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('warehouse_items')
        .select('*')
        .eq('clinic_id', clinic.id)
        .is('deleted_at', null)
        .order('name', { ascending: true });

      if (error) throw error;
      if (import.meta.env.DEV) console.log('WarehouseContext: Fetched items:', data);
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching warehouse items:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const removeItems = async (itemsToRemove) => {
    try {
      for (const item of itemsToRemove) {
        // Validate inputs
        const currentQty = parseFloat(item.currentQuantity);
        const removeQty = parseFloat(item.quantityToRemove);
        
        if (isNaN(currentQty) || isNaN(removeQty)) {
          console.error('Invalid quantity values:', item);
          continue;
        }
        
        const newQuantity = currentQty - removeQty;
        
        // Ensure the new quantity is not negative and is a valid number
        const finalQuantity = Math.max(0, newQuantity);
        
        const { error } = await supabase
          .from('warehouse_items')
          .update({ 
            quantity: finalQuantity,
            updated_at: new Date().toISOString()
          })
          .eq('id', item.id);

        if (error) throw error;
      }
      
      await fetchItems();
      return { success: true, message: `Αφαιρέθηκαν ${itemsToRemove.length} προϊόντα` };
    } catch (err) {
      console.error('Error removing items:', err);
      return { success: false, message: 'Σφάλμα αφαίρεσης προϊόντων' };
    }
  };

  const setClinicData = (clinicData) => {
    if (import.meta.env.DEV) console.log('WarehouseContext: setClinicData called (no longer needed):', clinicData);
    // This function is no longer needed since we get clinic from AuthContext
  };

  useEffect(() => {
    if (clinic) {
      fetchItems();
    }
  }, [clinic]);

  const value = {
    items,
    loading,
    fetchItems,
    removeItems,
    setClinicData
  };

  return (
    <WarehouseContext.Provider value={value}>
      {children}
    </WarehouseContext.Provider>
  );
};
